import { Row, Col, Form, InputGroup, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { format } from 'date-fns'
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedStartDateEndDate } from "../../../slices/common";
import { useYearMonth } from "../../YearMonth";

const GstrHeader = ({ headerTitle, type }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { selectedStartDate, selectedEndDate } = useSelector((state) => state.common);
    const { setSelectedMonth, setSelectedYear } = useYearMonth();

    // Helper function to parse Redux date format to { month, year }
    const parseReduxDate = (dateStr) => {
        if (!dateStr) return { month: null, year: null };
        
        // If it's a string in "MM-YYYY" format
        if (typeof dateStr === 'string' && dateStr.includes('-')) {
            const [month, year] = dateStr.split('-');
            return { month: parseInt(month), year: parseInt(year) };
        }
        
        // If it's already an object with month/year properties
        if (typeof dateStr === 'object' && dateStr.month && dateStr.year) {
            return { month: dateStr.month, year: dateStr.year };
        }
        
        return { month: null, year: null };
    };

    // Helper function to format period object to "MM-YYYY" string for Redux
    const formatPeriodForRedux = (period) => {
        if (!period.month || !period.year) return null;
        const formattedMonth = period.month.toString().padStart(2, '0');
        return `${formattedMonth}-${period.year}`;
    };

    // Convert { month, year } to Date object for react-datepicker
    const periodToDate = (period) => {
        if (!period.month || !period.year) return null;
        return new Date(period.year, period.month - 1, 1); // month is 0-indexed
    };

    // Convert Date object to { month, year }
    const dateToPeriod = (date) => {
        if (!date) return { month: null, year: null };
        return {
            month: date.getMonth() + 1, // Convert to 1-based month
            year: date.getFullYear()
        };
    };

    // Initialize state with Redux values
    const [fromPeriod, setFromPeriod] = useState(() => parseReduxDate(selectedStartDate));
    const [toPeriod, setToPeriod] = useState(() => parseReduxDate(selectedEndDate));

    // Handle date picker changes
    const handlePeriodChange = (type, date) => {
        const period = dateToPeriod(date);
        if (type === "from") {
            setFromPeriod(period);
        } else {
            setToPeriod(period);
        }
    };

    // Validation logic - start date should be <= end date
    const validatePeriod = () => {
        if (!fromPeriod.month || !toPeriod.month) return false;

        const fromDate = new Date(fromPeriod.year, fromPeriod.month - 1, 1);
        const toDate = new Date(toPeriod.year, toPeriod.month - 1, 1);

        return fromDate <= toDate;
    };

    // Handle URL parameters initialization
    useEffect(() => {
        if (type === 'gstr' || type === 'gstr3b' || type === 'gstr9' || type === 'gstr9c') {
            const params = new URLSearchParams(location.search);
            const fromMonthParam = params.get("fromMonth");
            const fromYearParam = params.get("fromYear");
            const toMonthParam = params.get("toMonth");
            const toYearParam = params.get("toYear");

            if (fromMonthParam && fromYearParam) {
                const newFromPeriod = {
                    month: parseInt(fromMonthParam),
                    year: parseInt(fromYearParam)
                };
                setFromPeriod(newFromPeriod);
            }

            if (toMonthParam && toYearParam) {
                const newToPeriod = {
                    month: parseInt(toMonthParam),
                    year: parseInt(toYearParam)
                };
                setToPeriod(newToPeriod);
            }
        }
    }, [location.search, type]);

    // Handle form submission
    const handleSubmit = () => {
        if (!validatePeriod()) {
            alert("Start date must be before or equal to end date");
            return;
        }

        const params = new URLSearchParams(location.search);
        
        // Update URL parameters
        params.set("fromMonth", fromPeriod.month);
        params.set("fromYear", fromPeriod.year);
        params.set("toMonth", toPeriod.month);
        params.set("toYear", toPeriod.year);

        // Update URL
        navigate({
            pathname: location.pathname,
            search: `?${params.toString()}`
        }, { replace: true });

        // Update Redux state with string format "MM-YYYY" - THIS IS THE KEY FIX
        dispatch(setSelectedStartDateEndDate({
            start_date: formatPeriodForRedux(fromPeriod),
            end_date: formatPeriodForRedux(toPeriod),
        }));

        // Update legacy YearMonth hook if needed
        setSelectedMonth(fromPeriod.month);
        setSelectedYear(fromPeriod.year);
    };

    return (
        <Row className="align-items-center">
            {(type === 'gstr' || type === 'gstr3b' || type === 'gstr9' || type === 'gstr9c') &&
                <Col xs={12} lg={12}>
                    <div className="pa-choose-date">
                        <Row className="align-items-center g-2">
                            {/* From Period Section */}
                            <Col xs={12} sm={6} lg={1} className="text-sm-start text-lg-end">
                                <Form.Label className="mb-1 mb-lg-0 fw-medium">
                                    From Period:
                                </Form.Label>
                            </Col>
                            <Col xs={12} sm={6} lg={4} className="date datepicker datepicker1">
                                <DatePicker
                                    selected={periodToDate(fromPeriod)}
                                    onChange={(date) => handlePeriodChange("from", date)}
                                    dateFormat="MMMM yyyy"
                                    showMonthYearPicker
                                    customInput={
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <i className="las la-calendar"></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={fromPeriod.month && fromPeriod.year 
                                                    ? format(periodToDate(fromPeriod), 'MMMM yyyy') 
                                                    : ''
                                                }
                                                readOnly
                                                placeholder="Select month"
                                            />
                                        </InputGroup>
                                    }
                                />
                            </Col>
                            
                            {/* To Period Section */}
                            <Col xs={12} sm={6} lg={1} className="text-sm-start text-lg-start">
                                <Form.Label className="mb-1 mb-lg-0 fw-medium">
                                    To Period:
                                </Form.Label>
                            </Col>
                            <Col xs={12} sm={6} lg={4} className="date datepicker datepicker1">
                                <DatePicker
                                    selected={periodToDate(toPeriod)}
                                    onChange={(date) => handlePeriodChange("to", date)}
                                    dateFormat="MMMM yyyy"
                                    showMonthYearPicker
                                    customInput={
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <i className="las la-calendar"></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={toPeriod.month && toPeriod.year 
                                                    ? format(periodToDate(toPeriod), 'MMMM yyyy') 
                                                    : ''
                                                }
                                                readOnly
                                                placeholder="Select month"
                                            />
                                        </InputGroup>
                                    }
                                />
                            </Col>
                            
                            {/* Submit Button */}
                            <Col xs={12} sm={12} lg={2} className="d-grid d-lg-block">
                                <Button 
                                    onClick={handleSubmit}
                                    className="w-100 w-lg-auto"
                                    size="sm"
                                >
                                    Submit
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </Col>
            }
        </Row>
    );
};

export default GstrHeader;